import { useState, useEffect, useCallback } from 'react';
import logger from '../logger.js';

/**
 * A custom hook to manage localStorage
 * @param {string} key - The key to store the value under in localStorage.
 * @param {any} defaultValue - The default value to be used/returned if the key doesn't exist in localStorage.
 * @returns {[any, function]} - Returns the stored value and a function to update it.
 */
export default function useLocalStorage(key, defaultValue) {
    const [storedValue, setStoredValue] = useState(() => {
        try {
            const item = window.localStorage.getItem(key);
            return item !== null ? JSON.parse(item) : undefined;
        } catch (error) {
            logger.warn(`Error reading localStorage key “${key}”:`, error);
            return undefined;
        }
    });

    useEffect(() => {
        // Only set the value in localStorage if it exists
        if (storedValue !== undefined) {
            try {
                const valueToStore = JSON.stringify(storedValue);
                window.localStorage.setItem(key, valueToStore);
            } catch (error) {
                logger.warn(`Error setting localStorage key “${key}”:`, error);
            }
        }
    }, [key, storedValue]);

    const updateStoredValue = useCallback((value) => {
        setStoredValue((prev) => (typeof value === 'function' ? value(prev) : value));
    }, []);

    return [storedValue !== undefined ? storedValue : defaultValue, updateStoredValue];
}
